import { Injectable, inject } from '@angular/core';
import { UserService } from '@core/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetails, ViewportDetails } from '@shared/models/device-details';
import { getOrCreateBrowserDeviceId } from '@shared/utils/device.utils';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import packageJson from '../../../../package.json';

type DeviceNavigator = Navigator & {
  userAgentData?: {
    brands?: Array<{ brand: string; version: string; }>;
    mobile?: boolean;
    platform?: string;
  };
};

type DeviceInfoSnapshot = {
  browser?: string;
  browser_version?: string;
  os?: string;
  os_version?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DeviceDetailsService {
  private readonly translate = inject(TranslateService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly userService = inject(UserService);

  private readonly notificationsTokenStorageKey = 'bueno.notifications.token';

  buildDeviceDetails(firebaseToken?: string): DeviceDetails | null {
    if (!this.isBrowser()) {
      return null;
    }

    const nav = navigator as DeviceNavigator;
    const deviceInfo = this.getDeviceInfoSnapshot();
    const userAgent = nav.userAgent ?? '';
    const platform = this.resolveSystemDetails(nav, deviceInfo);
    const browserLanguage = nav.language || this.translate.getBrowserLang() || 'en';
    const theme = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    const viewport: ViewportDetails = {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    };
    const isMobileBrowser = this.resolveIsMobileBrowser(nav, userAgent);
    const brandDetails = this.resolveBrandDetails(nav, deviceInfo, userAgent);
    const deviceToken = this.getNotificationsToken(firebaseToken);
    return {
      deviceToken,
      deviceId: getOrCreateBrowserDeviceId(),
      appVersion: packageJson.version ?? '',
      systemDetails: platform,
      brandDetails,
      isTablet: this.deviceService.isTablet(),
      theme,
      userAgent,
      browserLanguage,
      isMobileBrowser,
      viewport,
    };
  }

  storeDeviceDetails(deviceDetails?: DeviceDetails, firebaseToken?: string): Observable<boolean> {
    const details = deviceDetails ?? this.buildDeviceDetails(firebaseToken);
    if (!details) {
      return of(false);
    }

    return this.userService.registerDevice(details).pipe(map(() => true));
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof navigator !== 'undefined';
  }

  private getNotificationsToken(firebaseToken?: string): string | undefined {
    if (!this.isBrowser()) {
      return undefined;
    }

    try {
      let token = window.localStorage.getItem(this.notificationsTokenStorageKey);
      if (!token && firebaseToken) {
        token = firebaseToken;
        window.localStorage.setItem(this.notificationsTokenStorageKey, token);
      }
      return token ? token : undefined;
    } catch {
      return undefined;
    }
  }

  private getDeviceInfoSnapshot(): DeviceInfoSnapshot {
    try {
      return this.deviceService.getDeviceInfo() as DeviceInfoSnapshot;
    } catch {
      return {};
    }
  }

  private resolveSystemDetails(nav: DeviceNavigator, deviceInfo: DeviceInfoSnapshot): string {
    const userAgentPlatform = nav.userAgentData?.platform?.trim();
    if (userAgentPlatform) {
      return userAgentPlatform;
    }

    const detectorPlatform = [deviceInfo.os, deviceInfo.os_version].filter(Boolean).join(' ').trim();
    if (detectorPlatform) {
      return detectorPlatform;
    }

    return nav.platform?.trim() || 'Unknown';
  }

  private resolveBrandDetails(
    nav: DeviceNavigator,
    deviceInfo: DeviceInfoSnapshot,
    userAgent: string,
  ): string {
    const userAgentBrands = nav.userAgentData?.brands?.length
      ? nav.userAgentData.brands.map(brand => `${brand.brand} ${brand.version}`.trim()).join(', ').trim()
      : '';
    if (userAgentBrands) {
      return userAgentBrands;
    }

    const detectorBrand = [deviceInfo.browser, deviceInfo.browser_version].filter(Boolean).join(' ').trim();
    if (detectorBrand) {
      return detectorBrand;
    }

    return this.parseBrowserFromUserAgent(userAgent) || 'Unknown';
  }

  private resolveIsMobileBrowser(nav: DeviceNavigator, userAgent: string): boolean {
    if (typeof nav.userAgentData?.mobile === 'boolean') {
      return nav.userAgentData.mobile;
    }

    if (this.deviceService.isMobile()) {
      return true;
    }

    return /android|iphone|ipod|iemobile|blackberry|opera mini|mobile|fennec|webos/i.test(userAgent);
  }

  private parseBrowserFromUserAgent(userAgent: string): string {
    const browserMatchers: Array<{ name: string; regex: RegExp; versionGroup?: number; }> = [
      { name: 'Firefox', regex: /(firefox|fxios|waterfox|librewolf|floorp)\/([\d.]+)/i, versionGroup: 2 },
      { name: 'Edge', regex: /(edg|edgios|edga)\/([\d.]+)/i, versionGroup: 2 },
      { name: 'Opera', regex: /(opr|opera)\/([\d.]+)/i, versionGroup: 2 },
      { name: 'Chrome', regex: /(chrome|crios)\/([\d.]+)/i, versionGroup: 2 },
      { name: 'Safari', regex: /version\/([\d.]+).*safari/i, versionGroup: 1 },
    ];

    for (const matcher of browserMatchers) {
      const match = userAgent.match(matcher.regex);
      if (match) {
        const version = match[matcher.versionGroup ?? 1];
        return `${matcher.name}${version ? ` ${version}` : ''}`.trim();
      }
    }

    return '';
  }
}
